import { useCallback, useEffect, useState } from "react";

import { fetchConfig, updateConfig, type ConfigResponse } from "../api/config";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; cfg: ConfigResponse }
  | { kind: "error"; message: string };

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="settings-row">
      <span className="settings-row-label">{label}</span>
      <span className="settings-row-value">{children}</span>
    </div>
  );
}

export function SettingsView() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [busy, setBusy] = useState<"model" | null>(null);
  const [flash, setFlash] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const cfg = await fetchConfig();
      setState({ kind: "ready", cfg });
    } catch (e) {
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleModelChange = useCallback(async (modelId: string) => {
    setBusy("model");
    setFlash(null);
    try {
      const cfg = await updateConfig({ claude_model: modelId });
      setState({ kind: "ready", cfg });
      setFlash({ kind: "ok", text: `Model set to ${cfg.claude_model}` });
    } catch (e) {
      setFlash({
        kind: "err",
        text: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(null);
    }
  }, []);

  if (state.kind === "loading") {
    return <div className="page-body">Loading settings…</div>;
  }
  if (state.kind === "error") {
    return (
      <div className="page-body">
        <div className="settings-error">Failed to load /api/config: {state.message}</div>
      </div>
    );
  }

  const cfg = state.cfg;

  return (
    <div className="page-body">
      <div className="page-head">
        <div className="page-title">
          <h1>Settings</h1>
          <div className="page-sub">
            Workspace configuration, Anthropic credentials, and app health.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="head"><span className="title">Anthropic API</span></div>
        <div className="body">
          <Row label="API key">
            {cfg.api_key_present ? (
              <span className="chip">
                <span className="dot" />
                <span>configured via environment · …{cfg.api_key_hint}</span>
              </span>
            ) : (
              <span className="chip warn">
                <span className="dot" />
                <span>not configured</span>
              </span>
            )}
          </Row>
          <Row label="How to set">
            <span>
              Supplied through the deploy environment as{" "}
              <span className="mono">ANTHROPIC_API_KEY</span> — a gitignored{" "}
              <span className="mono">.env</span> file locally, or SSM Parameter
              Store on AWS. It is not stored by the app and survives redeploys.
            </span>
          </Row>
          <Row label="Active model">
            {cfg.claude_model_options.length > 0 ? (
              <select
                className="select"
                value={cfg.claude_model ?? ""}
                onChange={(e) => void handleModelChange(e.target.value)}
                disabled={busy !== null}
              >
                {cfg.claude_model_options.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                    {m.tier ? ` · ${m.tier}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <span className="mono">{cfg.claude_model ?? "—"}</span>
            )}
          </Row>
          {flash ? (
            <Row label="">
              <span className={flash.kind === "ok" ? "settings-flash-ok" : "settings-flash-err"}>
                {flash.text}
              </span>
            </Row>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="head"><span className="title">Workspace</span></div>
        <div className="body">
          <Row label="Workspace"><span className="mono">{cfg.workspace ?? "—"}</span></Row>
          <Row label="Research dir"><span className="mono">{cfg.research_dir ?? "—"}</span></Row>
          <Row label="Setups dir"><span className="mono">{cfg.setups_dir ?? "—"}</span></Row>
          <Row label="Default segment">{cfg.default_segment ?? "—"}</Row>
          <Row label="Default iterations">{cfg.default_iterations ?? "—"}</Row>
        </div>
      </div>

    </div>
  );
}
